import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Heart, Sparkles, User, Calendar, ArrowLeft, Lock, Copy, Trash2, LogIn } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  getCategoryDescription, 
  LIFE_PATH_DESCRIPTIONS, 
  BIRTH_DAY_DESCRIPTIONS, 
  DESTINY_NUMBER_DESCRIPTIONS,
  EXPRESSION_DESCRIPTIONS,
  SOUL_URGE_DESCRIPTIONS,
  PERSONALITY_DESCRIPTIONS
} from '@/utils/numerologyDescriptions';
import { calculateExpression, calculateSoul, calculatePersonality } from '@/utils/numerologyCalculator';

const OWNER_EMAIL = 'info@skaidracreative.co.uk';

// Reduce number to single digit or master number
const reduceToSingleOrMaster = (num) => {
  let n = parseInt(num);
  while (n > 9 && n !== 11 && n !== 22 && n !== 33 && n !== 44) {
    n = String(n).split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  }
  return n;
};

// Calculate Life Path from full date
const calculateLifePath = (day, month, year) => {
  const d = reduceToSingleOrMaster(day);
  const m = reduceToSingleOrMaster(month);
  const y = reduceToSingleOrMaster(year);
  return reduceToSingleOrMaster(d + m + y);
};

// Calculate Birth Day Number (just the day reduced)
const calculateBirthDay = (day) => {
  return reduceToSingleOrMaster(day);
};

// Calculate Destiny Number (Month + Day)
const calculateDestiny = (day, month) => {
  const sum = parseInt(day) + parseInt(month);
  return reduceToSingleOrMaster(sum);
};

// Result Card Component
const ResultCard = ({ number, title, subtitle, description, color = 'amber', icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card className={`p-6 border-${color}-500/30 bg-gradient-to-br from-${color}-500/10 to-transparent`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-full bg-${color}-500/20`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-4xl font-display font-bold text-${color}-400`}>{number}</span>
            <div>
              <h3 className="font-semibold text-lg text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">
            {description}
          </p>
        </div>
      </div>
    </Card>
  </motion.div>
);

export default function OwnerLivePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  
  // Form 1: Birth Date
  const [birthDate, setBirthDate] = useState({ day: '', month: '', year: '' });
  const [dateResults, setDateResults] = useState(null);
  
  // Form 2: Name
  const [nameData, setNameData] = useState({ firstName: '', lastName: '' });
  const [nameResults, setNameResults] = useState(null);

  // Check authorization when user changes
  useEffect(() => {
    console.log('[OWNER-LIVE] Auth loading:', authLoading);
    console.log('[OWNER-LIVE] User:', user);
    console.log('[OWNER-LIVE] User email:', user?.email);
    
    if (!authLoading && user?.email) {
      const isOwner = user.email.toLowerCase() === OWNER_EMAIL.toLowerCase();
      console.log('[OWNER-LIVE] Is owner:', isOwner);
      setAuthorized(isOwner);
    }
  }, [user, authLoading]);

  // Calculate Date Results
  const handleDateCalculate = () => {
    const { day, month, year } = birthDate;
    if (!day || !month || !year) {
      toast.error('Įveskite pilną gimimo datą');
      return;
    }

    const lifePath = calculateLifePath(day, month, year);
    const birthDayNum = calculateBirthDay(day);
    const destiny = calculateDestiny(day, month);

    const lifePathDesc = getCategoryDescription('lifePath', lifePath);
    const birthDayDesc = getCategoryDescription('birthDay', birthDayNum);
    const destinyDesc = getCategoryDescription('destiny', destiny);

    setDateResults({
      lifePath: { number: lifePath, desc: lifePathDesc },
      birthDay: { number: birthDayNum, desc: birthDayDesc },
      destiny: { number: destiny, desc: destinyDesc }
    });
  };

  // Calculate Name Results
  const handleNameCalculate = () => {
    const { firstName, lastName } = nameData;
    if (!firstName || !lastName) {
      toast.error('Įveskite vardą ir pavardę');
      return;
    }

    const fullName = `${firstName} ${lastName}`;
    const expression = calculateExpression(fullName);
    const soul = calculateSoul(fullName);
    const personality = calculatePersonality(fullName);

    const expressionDesc = getCategoryDescription('expression', expression);
    const soulDesc = getCategoryDescription('soulUrge', soul);
    const personalityDesc = getCategoryDescription('personality', personality);

    setNameResults({
      expression: { number: expression, desc: expressionDesc },
      soul: { number: soul, desc: soulDesc },
      personality: { number: personality, desc: personalityDesc }
    });
  };

  // Clear results
  const clearDateResults = () => {
    setDateResults(null);
    setBirthDate({ day: '', month: '', year: '' });
  };

  const clearNameResults = () => {
    setNameResults(null);
    setNameData({ firstName: '', lastName: '' });
  };

  // Copy result to clipboard
  const copyResult = (title, number, description) => {
    const text = `${title}: ${number}\n\n${description}`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Nukopijuota!');
    });
  };

  // Loading state - waiting for auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Tikrinama prieiga...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <LogIn className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h1 className="text-2xl font-bold mb-2">Prašome prisijungti</h1>
          <p className="text-muted-foreground mb-4">
            Norėdami pasiekti šį puslapį, turite prisijungti.
          </p>
          <Button onClick={() => navigate('/')}>
            Eiti į pradžią
          </Button>
        </Card>
      </div>
    );
  }

  // Not authorized (wrong email)
  if (!authorized) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-2">Prieiga uždrausta</h1>
          <p className="text-muted-foreground mb-4">
            Šis puslapis prieinamas tik administratoriui.
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Prisijungęs: {user.email}
          </p>
          <Button onClick={() => navigate('/')}>
            Grįžti į pradžią
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Grįžti
          </Button>
          <h1 className="font-display text-xl text-primary">Owner's Live Dashboard</h1>
          <div className="w-24" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Form 1: Birth Date */}
          <div>
            <Card className="p-6 border-amber-500/30">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-amber-400" />
                <h2 className="text-xl font-semibold">Forma 1: Gimimo Data</h2>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Diena</label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="DD"
                    value={birthDate.day}
                    onChange={(e) => setBirthDate(prev => ({ ...prev, day: e.target.value }))}
                    className="text-center text-lg"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Mėnuo</label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    placeholder="MM"
                    value={birthDate.month}
                    onChange={(e) => setBirthDate(prev => ({ ...prev, month: e.target.value }))}
                    className="text-center text-lg"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Metai</label>
                  <Input
                    type="number"
                    min="1900"
                    max="2025"
                    placeholder="YYYY"
                    value={birthDate.year}
                    onChange={(e) => setBirthDate(prev => ({ ...prev, year: e.target.value }))}
                    className="text-center text-lg"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleDateCalculate}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Skaičiuoti
                </Button>
                {dateResults && (
                  <Button 
                    variant="outline" 
                    onClick={clearDateResults}
                    className="px-3"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </Card>

            {/* Date Results */}
            <AnimatePresence>
              {dateResults && (
                <motion.div 
                  className="mt-6 space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <ResultCard
                    number={dateResults.lifePath.number}
                    title="Gyvenimo Kelias"
                    subtitle="Jūsų pagrindinė misija"
                    description={dateResults.lifePath.desc?.description || dateResults.lifePath.desc?.character}
                    icon={Star}
                    color="amber"
                  />
                  <ResultCard
                    number={dateResults.birthDay.number}
                    title="Gimimo Dienos Skaičius"
                    subtitle="Charakteris ir Talentai"
                    description={dateResults.birthDay.desc?.description || dateResults.birthDay.desc?.character}
                    icon={Calendar}
                    color="blue"
                  />
                  <ResultCard
                    number={dateResults.destiny.number}
                    title="Likimo Skaičius"
                    subtitle="Mėnuo + Diena"
                    description={dateResults.destiny.desc?.description || dateResults.destiny.desc?.character}
                    icon={Sparkles}
                    color="purple"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Form 2: Name */}
          <div>
            <Card className="p-6 border-emerald-500/30">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-emerald-400" />
                <h2 className="text-xl font-semibold">Forma 2: Vardas ir Pavardė</h2>
              </div>
              
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Vardas</label>
                  <Input
                    type="text"
                    placeholder="Įveskite vardą"
                    value={nameData.firstName}
                    onChange={(e) => setNameData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="text-lg"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Pavardė</label>
                  <Input
                    type="text"
                    placeholder="Įveskite pavardę"
                    value={nameData.lastName}
                    onChange={(e) => setNameData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="text-lg"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleNameCalculate}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                >
                  Skaičiuoti
                </Button>
                {nameResults && (
                  <Button 
                    variant="outline" 
                    onClick={clearNameResults}
                    className="px-3"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </Card>

            {/* Name Results */}
            <AnimatePresence>
              {nameResults && (
                <motion.div 
                  className="mt-6 space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <ResultCard
                    number={nameResults.expression.number}
                    title="Išraiškos Skaičius"
                    subtitle="Jūsų talentai ir gebėjimai"
                    description={nameResults.expression.desc?.description || nameResults.expression.desc?.character}
                    icon={Sparkles}
                    color="emerald"
                  />
                  <ResultCard
                    number={nameResults.soul.number}
                    title="Sielos Troškimas"
                    subtitle="Ko jūsų siela trokšta"
                    description={nameResults.soul.desc?.description || nameResults.soul.desc?.character}
                    icon={Heart}
                    color="pink"
                  />
                  <ResultCard
                    number={nameResults.personality.number}
                    title="Asmenybės Skaičius"
                    subtitle="Kaip kiti jus mato"
                    description={nameResults.personality.desc?.description || nameResults.personality.desc?.character}
                    icon={User}
                    color="cyan"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </main>
    </div>
  );
}
